import React, { Fragment } from 'react';
import { TypedDocumentNode, gql, useQuery } from '@apollo/client';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Loading, Stack, Constrain } from '@moda/om';
import { compact, SECONDS_TO_MILLISECONDS } from '@moda/portal-stanchions';
import { isAfter } from 'date-fns';
import {
  VARIANT_CELL_FRAGMENT,
  LOOK_CELL_FRAGMENT,
  EDITORIAL_CELL_FRAGMENT,
  CarouselCell,
  LazyGrid
} from '../../components/Grid';
import {
  HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT,
  useHasAccessToProductOrTrunkshow
} from '../../hooks/useHasAccessToProductOrTrunkshow';
import { TRUNKSHOW_TITLE_FRAGMENT, useTrunkshowTitle } from '../../hooks/useTrunkshowTitle';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { EnableLazyLoad } from '../../components/LazyLoad';
import { LooksModule } from '../../components/LooksModule';
import { PageMeta } from '../../components/PageMeta';
import { TrunkshowEndedHeader } from '../../components/TrunkshowEndedHeader';
import { TrunkshowPageSubtitle } from '../../components/TrunkshowPageSubtitle';
import { REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS } from '../../constants';
import { hrefFor } from '../../routers';
import NotFoundPage from '../NotFoundPage';
import {
  NewTrunkshowPageQuery,
  NewTrunkshowPageQueryVariables,
  NewTrunkshowPageModulesQuery,
  NewTrunkshowPageModulesQueryVariables
} from '../../generated/types';
import { useCountry } from '../../components/CountrySelector';
import {
  TrunkshowHeaderModule,
  TRUNKSHOW_HEADER_MODULE_TRUNKSHOW_FRAGMENT
} from './TrunkshowHeaderModule';
import { RedirectExpiredTrunkshow } from './RedirectExpiredTrunkshow';
import DEFAULT_TRUNKSHOW_HEADER_MODULE from './DefaultTrunkshowHeaderModule';
import './TrunkshowPage.scss';

const NEW_TRUNKSHOW_PAGE_QUERY: TypedDocumentNode<
  NewTrunkshowPageQuery,
  NewTrunkshowPageQueryVariables
> = gql`
  query NewTrunkshowPageQuery($slug: String!, $ruleContexts: [String]!, $countryCode: String) {
    trunkshowPage: new_trunkshow_page(slug: $slug, client_country_code: $countryCode) {
      trunkshow {
        name
        description
        designerNames: designer_names
        designerSlugs: designer_slugs
        designersData: limited_designer_data {
          id
          name
          slug
        }
        seasonName: season_name
        activeEndI: active_end_i
        ...HasAccessToTrunkshowFragment
        ...TrunkshowHeaderModuleTrunkshowFragment
        ...TrunkshowTitleFragment
      }
      products(rule_contexts: $ruleContexts) {
        ... on Variant {
          ...VariantCellFragment
        }
        ... on Slide {
          ...EditorialCellFragment
        }
      }
      looks(rule_contexts: $ruleContexts) {
        ... on Look {
          ...LookCellFragment
        }
        ... on Slide {
          ...EditorialCellFragment
        }
      }
    }
  }
  ${HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT}
  ${VARIANT_CELL_FRAGMENT}
  ${LOOK_CELL_FRAGMENT}
  ${EDITORIAL_CELL_FRAGMENT}
  ${TRUNKSHOW_HEADER_MODULE_TRUNKSHOW_FRAGMENT}
  ${TRUNKSHOW_TITLE_FRAGMENT}
`;

const NEW_TRUNKSHOW_PAGE_MODULES_QUERY: TypedDocumentNode<
  NewTrunkshowPageModulesQuery,
  NewTrunkshowPageModulesQueryVariables
> = gql`
  query NewTrunkshowPageModulesQuery($slug: String!, $versionId: Int, $countryCode: String!) {
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      meta_description
    }
  }
  ${PUMO_MODULE_FRAGMENT}
`;

export const NewTrunkshowPage: React.FC = () => {
  const location = useLocation();
  const {
    trunkshowSlug = '',
    designerSlug = '',
    season = '',
    vertical = 'women'
  } = useParams<{
    trunkshowSlug?: string;
    designerSlug?: string;
    season?: string;
    vertical?: string;
  }>();

  const { country } = useCountry();
  const slug = trunkshowSlug || `${designerSlug}-${season}`;

  const { data: trunkshowPageData, loading: trunkshowPageLoading } = useQuery(
    NEW_TRUNKSHOW_PAGE_QUERY,
    {
      variables: {
        slug,
        countryCode: country.alpha2Code,
        ruleContexts: []
      }
    }
  );

  const { data: trunkshowModulesData, loading: trunkshowModulesLoading } = useQuery(
    NEW_TRUNKSHOW_PAGE_MODULES_QUERY,
    {
      variables: {
        slug,
        countryCode: country.alpha2Code
      }
    }
  );

  const { hasAccess, loading: loadingAccess } = useHasAccessToProductOrTrunkshow(
    trunkshowPageData?.trunkshowPage?.trunkshow
  );

  const trunkshow = trunkshowPageData?.trunkshowPage?.trunkshow;
  const title = useTrunkshowTitle(trunkshow);
  const description = `Preorder the ${title} on Moda Operandi.`;

  if (trunkshowPageLoading || trunkshowModulesLoading || !trunkshowPageData || loadingAccess)
    return <Loading />;

  if (!trunkshow) return <NotFoundPage />;

  // if the user does not have access to the trunkshow return a 404
  if (!hasAccess) return <NotFoundPage />;

  const hasExpiredAlongTimeAgo = trunkshow.activeEndI
    ? isAfter(
        new Date(),
        trunkshow.activeEndI * SECONDS_TO_MILLISECONDS + REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS
      )
    : false;

  // if the trunkshow has expired a long time ago, redirect to the designer page
  if (hasExpiredAlongTimeAgo) {
    return <RedirectExpiredTrunkshow designerSlug={trunkshow.designerSlugs?.[0]} />;
  }

  const isExpired = trunkshow.activeEndI
    ? isAfter(new Date(), trunkshow.activeEndI * SECONDS_TO_MILLISECONDS)
    : false;

  // if the url is constructed incorrectly, redirect to the correct url
  if (hrefFor.TrunkshowPage({ vertical, trunkshowSlug: slug }) !== location.pathname)
    return <Redirect to={hrefFor.TrunkshowPage({ vertical, trunkshowSlug: slug })} />;

  const headerModules = compact(
    trunkshowModulesData?.displayPage?.targetBuckets.find(({ name }) => name === 'header')?.modules
  );

  return (
    <>
      <PageMeta title={title} description={description} />

      <Stack className="TrunkshowPage" space={6}>
        {isExpired && <TrunkshowEndedHeader trunkshow={trunkshow} />}

        {headerModules.length === 0 && (
          <Constrain>
            <TrunkshowHeaderModule
              trunkshowHeaderModulePumo={{
                ...DEFAULT_TRUNKSHOW_HEADER_MODULE,
                __typename: 'SinglePumoModule',
                slides: [
                  {
                    ...DEFAULT_TRUNKSHOW_HEADER_MODULE.slides[0],
                    __typename: 'Slide',
                    slideText: {
                      ...DEFAULT_TRUNKSHOW_HEADER_MODULE.slides[0].slideText,
                      __typename: 'SlideText',
                      headlineText: { __typename: 'TextObject', body: trunkshow.name },
                      taglineText: { __typename: 'TextObject', body: trunkshow.description }
                    }
                  }
                ]
              }}
              trunkshowHeaderModuleTrunkshow={trunkshow}
            />
          </Constrain>
        )}

        {headerModules.map((headerModule, index) => (
          <Fragment key={index}>
            {headerModule.__typename === 'SinglePumoModule' && (
              <Constrain>
                <TrunkshowHeaderModule
                  key={headerModule.id}
                  trunkshowHeaderModulePumo={headerModule}
                  trunkshowHeaderModuleTrunkshow={trunkshow}
                />
              </Constrain>
            )}
          </Fragment>
        ))}
        <EnableLazyLoad>
          <LooksModule
            carousels={[
              {
                __typename: 'CarouselPumoModule',
                cells: compact(trunkshowPageData.trunkshowPage?.looks),
                layoutType: 1,
                name: `${trunkshowPageData.trunkshowPage?.trunkshow?.name}-Looks`,
                id: 0,
                modelId: '0',
                platform: ['desktop', 'mobile']
              }
            ]}
          />
          <Stack space={6}>
            <TrunkshowPageSubtitle>Shop Products</TrunkshowPageSubtitle>

            <Constrain>
              <LazyGrid>
                {compact(trunkshowPageData.trunkshowPage?.products).map((cell, index) => (
                  <CarouselCell key={index} carouselCell={cell} />
                ))}
              </LazyGrid>
            </Constrain>
          </Stack>
        </EnableLazyLoad>
      </Stack>
    </>
  );
};
